/**
 * Component Registry for Sunrise Management Theme
 * 
 * Extends the base preferred theme registry with Sunrise Management specific overrides.
 */

import type { ComponentRegistry } from '@vibesites/runtime';
import { registry as baseRegistry } from '@vibesites/theme-preferred/registry';
import { Footer } from './components/Footer';
import { ApplyNow } from './components/ApplyNow';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { SpecialOfferBanner } from './components/SpecialOfferBanner';
import { PropertyOverview } from './components/PropertyOverview';
import { FloorplansList } from './components/FloorplansList';
import { ReviewsCallout } from './components/ReviewsCallout';
import { FloorPlansHero } from './components/FloorPlansHero';
import { AmenitiesHero } from './components/AmenitiesHero';
import { AmenitiesList } from './components/AmenitiesList';
import { VirtualTourHero } from './components/VirtualTourHero';
import { VirtualTourEmbed } from './components/VirtualTourEmbed';
import { GalleryHero } from './components/GalleryHero';
import { Gallery } from './components/Gallery';
import { NeighborhoodHero } from './components/NeighborhoodHero';
import { ContactHero } from './components/ContactHero';
import { ResidentAccessHero } from './components/ResidentAccessHero';
import { ResidentAccess } from './components/ResidentAccess';
import { ScheduleATourHero } from './components/ScheduleATourHero';
import { ContactUs } from './components/ContactUs';
import { ScheduleATour } from './components/ScheduleATour';
import { ReviewsHero } from './components/ReviewsHero';
import { PetPolicyHero } from './components/PetPolicyHero';
import { InteractiveMapHero } from './components/InteractiveMapHero';
import { PrivacyPolicyHero } from './components/PrivacyPolicyHero';
import { SitemapHero } from './components/SitemapHero';
import { SitemapContent } from './components/SitemapContent';
import { AccessibilityHero } from './components/AccessibilityHero';
import { ApplyNowHero } from './components/ApplyNowHero';
import { ThankYouHero } from './components/ThankYouHero';
import { ThankYouContent } from './components/ThankYouContent';
import { HyperlocalHero } from './components/HyperlocalHero';

/**
 * Sunrise Management specific component overrides.
 * These components have been customized for the Sunrise Management brand.
 * 
 * Example:
 * import { Footer } from './components/Footer';
 * 
 * const sunrisemanagementOverrides: Partial<ComponentRegistry> = {
 *   Footer,
 * };
 */
const sunrisemanagementOverrides: Partial<ComponentRegistry> = {
  Footer,
  ApplyNow,
  Header,
  Hero,
  SpecialOfferBanner,
  PropertyOverview,
  FloorplansList,
  ReviewsCallout,
  // Interior page heroes — solid 30% black overlay (no gradient), matching the home Hero.
  FloorPlansHero,
  AmenitiesHero,
  AmenitiesList,
  VirtualTourHero,
  VirtualTourEmbed,
  GalleryHero,
  Gallery,
  NeighborhoodHero,
  ContactHero,
  ResidentAccessHero,
  ResidentAccess,
  ScheduleATourHero,
  ContactUs,
  ScheduleATour,
  ReviewsHero,
  PetPolicyHero,
  InteractiveMapHero,
  PrivacyPolicyHero,
  SitemapHero,
  SitemapContent,
  AccessibilityHero,
  ApplyNowHero,
  ThankYouHero,
  ThankYouContent,
  HyperlocalHero,
};

/**
 * Final merged registry for Sunrise Management properties.
 * Inherits all components from the preferred theme, with Sunrise Management overrides.
 */
export const registry: ComponentRegistry = {
  ...baseRegistry,
  ...sunrisemanagementOverrides,
};

export default registry;
